// archetypes27.js
// Utility para mapear as 27 combinações E/A/S -> archetype (label, emoji, description, clan)
// Entrada: traits = [emotionality, activity, sociability] valores 0..100 (ou percentuais)
// Saída: { key, label, emoji, description, clan: { key, label } }
import templatePt from "../data/pt/temperament_templates.json";
import templateEn from "../data/en/temperament_templates.json";

// Mapeamento direto por combinação "e_a_s" (low|mid|high)
const MAP = {
  // Emocionalidade = low
  low_low_low: "guardian",
  // E=mid, A=low, S=low
  mid_low_low: "sentinel",
  // E=mid A=low S=mid
  mid_low_mid: "evaluator",
  // E=mid A=mid S=low
  mid_mid_low: "thinker",
  // E=mid A=mid S=mid
  mid_mid_mid: "navigator",
  // E=high A=low S=low
  high_low_low: "artist",
  // E=high A=low S=mid
  high_low_mid: "dreamer",
  // E=high A=low S=high
  high_low_high: "poet",
  // E=high A=mid S=low
  high_mid_low: "mentor",
  // E=high A=mid S=mid
  high_mid_mid: "sensitive",
  // E=high A=mid S=high
  high_mid_high: "counselor",
  // E=high A=high S=low
  high_high_low: "vigilant",
  // E=high A=high S=mid
  high_high_mid: "encourager",
  // E=high A=high S=high
  high_high_high: "charismatic",
  // E=low A=mid S=low
  low_mid_low: "architect",
  // E=low A=mid S=mid
  low_mid_mid: "logico",
  // E=low A=high S=low
  low_high_low: "forger",
  // E=low A=high S=mid
  low_high_mid: "producer",
  // E=low A=high S=high
  low_high_high: "commander",
  // E=mid A=high S=low
  mid_high_low: "explorer",
  // E=mid A=high S=mid
  mid_high_mid: "artificer",
  // E=mid A=high S=high
  mid_high_high: "driver",
  // E=low A=low S=mid
  low_low_mid: "observer",
  // E=low A=low S=high
  low_low_high: "strategist",
  // E=low A=mid S=high
  low_mid_high: "diplomat",
  // E=mid A=low S=high
  mid_low_high: "connector",
  // E=mid A=mid S=high
  mid_mid_high: "coordinator",
};

// classify numeric value into low|mid|high
function classifyVal(v) {
  const n = Number(v) || 0;
  if (n > 60) return "high";
  if (n >= 40) return "mid";
  return "low";
}

/**
 * getArchetypeFromTraits
 * @param {Array<number>} traits - [emotionality, activity, sociability] (0..100)
 * @returns {object} { key, label, emoji, description, clan }
 *
 * This function implements your clinical rule:
 * - if any trait == high -> choose profile where that trait is high (priority E > A > S if multiple highs)
 * - else if any trait == mid -> choose profile where that trait is mid (priority E > A > S)
 * - else -> all low -> goes to Clã Calmaria (Guardião)
 *
 * The 27 possible mappings are included in MAP.
 */
export function getArchetypeFromTraits(traits = [0, 0, 0], lang) {
  const [em, ac, so] = traits;
  const E = classifyVal(em);
  const A = classifyVal(ac);
  const S = classifyVal(so);

  const template = lang?.startsWith("pt") ? templatePt : templateEn;

  // if any high -> prioritize E > A > S
  if (E === "high" || A === "high" || S === "high") {
    // prefer E if high, else A if high, else S
    const pick = E === "high" ? "high" : A === "high" ? "high" : "high";
    // but we need the full key: E/A/S levels
    const key = `${E}_${A}_${S}`;
    if (MAP[key]) return template.profiles[MAP[key]];
    // fallback: try to prefer the trait that is high in a constructed key
    if (E === "high") {
      const k = `high_${A}_${S}`;
      if (MAP[k]) return MAP[k];
    }
    if (A === "high") {
      const k = `${E}_high_${S}`;
      if (MAP[k]) return MAP[k];
    }
    if (S === "high") {
      const k = `${E}_${A}_high`;
      if (MAP[k]) return MAP[k];
    }
  }

  // no high; if any mid -> priority E > A > S
  if (E === "mid" || A === "mid" || S === "mid") {
    const key = `${E}_${A}_${S}`;
    if (MAP[key]) return template.profiles[MAP[key]];
    if (E === "mid") {
      const k = `mid_${A}_${S}`;
      if (MAP[k]) return template.profiles[MAP[k]];
    }
    if (A === "mid") {
      const k = `${E}_mid_${S}`;
      if (MAP[k]) return template.profiles[MAP[k]];
    }
    if (S === "mid") {
      const k = `${E}_${A}_mid`;
      if (MAP[k]) return template.profiles[MAP[k]];
    }
  }

  // all low - calmaria: default to Guardião (we already mapped low_low_low to Guardião)
  return MAP["low_low_low"];
}
